import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { InfoRow, MetricCard } from '../components/servers/ServerWidgets';
import {
  BackLink,
  HitsTable,
  IdentityCard,
  Section,
  VerdictBanner,
  backendLabel,
  formatWhen,
} from '../components/security/SecurityDetail';
import { eventTypeLabel, sourceLabel, statusLabel } from '../components/security/securityLabels';
import {
  apiCreateCybersecurityMonitored,
  apiGetCybersecurityIpDetail,
  apiRevertCybersecurityMonitored,
} from '../api/client';

export default function SecurityIpDetailPage() {
  const { source, ip } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      setData(await apiGetCybersecurityIpDetail(source, ip));
    } catch (e) {
      setError(e.message || 'Failed to load IP detail');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [source, ip]);

  const onMonitor = async () => {
    setSaving(true);
    try {
      await apiCreateCybersecurityMonitored(source, { actor_key: ip, reason: 'admin from IP detail' });
      await load();
    } catch (e) {
      setError(e.message || 'Could not add this address to the watch list');
    } finally {
      setSaving(false);
    }
  };

  const onRevert = async (id) => {
    try {
      await apiRevertCybersecurityMonitored(source, id);
      await load();
    } catch (e) {
      setError(e.message || 'Revert failed');
    }
  };

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(ip);
    } catch (e) {
      setError('Could not copy IP');
    }
  };

  const verdict = data?.verdict || {};
  const lastHit = data?.last_hit;
  const primaryUser = (data?.users || [])[0];

  return (
    <AppLayout>
      <div className="page-content">
        <BackLink />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', margin: '12px 0 8px' }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{ip}</h1>
            <p style={{ color: '#64748b', marginTop: 4 }}>{backendLabel(source)} · Address investigation</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={copyIp} style={btnStyle}>Copy address</button>
            <button type="button" disabled={saving} onClick={onMonitor} style={{ ...btnStyle, background: '#0f766e', color: '#fff', border: 0 }}>
              {saving ? 'Saving…' : 'Add to watch list'}
            </button>
          </div>
        </div>

        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        {loading && <p style={{ color: '#64748b' }}>Loading IP detail…</p>}

        {!loading && data && (
          <>
            <VerdictBanner level={verdict.level} text={verdict.summary} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              <MetricCard label="Blocks" value={verdict.block_count || 0} sub={`threshold ${verdict.threshold || 200}`} />
              <MetricCard label="Why blocked" value={(verdict.sources || []).map(sourceLabel).join(', ') || '—'} />
              <MetricCard label="On the watch list" value={verdict.monitored ? 'Yes' : 'No'} />
              <MetricCard label="Last hit" value={formatWhen(lastHit?.created_at)} sub={lastHit ? `${lastHit.method || ''} ${lastHit.path || ''}` : ''} />
            </div>

            <Section title="Who used this IP">
              {(data.users || []).length === 0 && <IdentityCard user={null} />}
              {(data.users || []).map((user) => (
                <div key={user.id || user.email || user.label} style={{ marginBottom: 12 }}>
                  <IdentityCard user={user} />
                </div>
              ))}
              {primaryUser && lastHit && (
                <p style={{ color: '#475569', fontSize: 14, marginTop: 8 }}>
                  Latest: {primaryUser.label || 'Guest'} got {lastHit.http_status || '—'} on {lastHit.method} {lastHit.path}
                </p>
              )}
            </Section>

            <Section title="Last request">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                <InfoRow label="Method + path" value={lastHit ? `${lastHit.method || ''} ${lastHit.path || ''}` : '—'} />
                <InfoRow label="HTTP" value={lastHit?.http_status} />
                <InfoRow label="User-Agent" value={lastHit?.user_agent} />
                <InfoRow label="Session" value={lastHit?.session_id} />
                <InfoRow label="Device" value={lastHit?.device_id} />
                <InfoRow label="Login hint" value={lastHit?.login_hint} />
              </div>
            </Section>

            <Section title="Request timeline">
              <HitsTable hits={data.hits} source={source} />
            </Section>

            <Section title="Related events">
              {(data.events || []).length === 0 && <p style={{ color: '#64748b', margin: 0 }}>No events.</p>}
              {(data.events || []).map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => navigate(`/cybersecurity/event/${source}/${event.id}`)}
                  style={rowBtn}
                >
                  <strong>{eventTypeLabel(event.type)}</strong>
                  <span style={{ color: '#64748b' }}>{formatWhen(event.created_at)} · {event.action || '—'}</span>
                </button>
              ))}
            </Section>

            {(data.monitored || []).length > 0 && (
              <Section title="Monitoring">
                {data.monitored.map((row) => (
                  <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div>
                      <strong>{statusLabel(row.status)}</strong>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>{row.reason} · until {formatWhen(row.expires_at)}</p>
                    </div>
                    {row.status === 'active' && (
                      <button type="button" onClick={() => onRevert(row.id)} style={btnStyle}>Revert</button>
                    )}
                  </div>
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

const btnStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 13,
};

const rowBtn = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  textAlign: 'left',
  padding: '10px 0',
  border: 0,
  borderBottom: '1px solid #f1f5f9',
  background: 'transparent',
  cursor: 'pointer',
};
